import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Plus } from 'lucide-react';
import PeriodLockTable from '../../components/warehouse/PeriodLockTable';
import CreateLockModal from '../../components/warehouse/CreateLockModal';
import UnlockConfirmModal from '../../components/warehouse/UnlockConfirmModal';
import { warehouseAPI } from '../../api/warehouses';
import { Warehouse } from '../../types/entities';

const PeriodLockPage: React.FC = () => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<'locked' | 'unlocked' | undefined>();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [unlockModalLock, setUnlockModalLock] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      const data = await warehouseAPI.getAll({ status: 'ACTIVE' });
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleUnlockSuccess = () => {
    setUnlockModalLock(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleUnlockRequest = (lock: any) => {
    setUnlockModalLock(lock);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Lock className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800">
            {t('warehouse.periodLock.title')}
          </h1>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-5 h-5" />
          {t('warehouse.periodLock.createLock')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Warehouse Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.warehouse')}
            </label>
            <select
              value={selectedWarehouse || ''}
              onChange={(e) => setSelectedWarehouse(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('warehouse.periodLock.allWarehouses')}</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} - {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.status')}
            </label>
            <select
              value={selectedStatus || ''}
              onChange={(e) => setSelectedStatus(e.target.value as any || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('warehouse.periodLock.allStatuses')}</option>
              <option value="locked">{t('warehouse.periodLock.locked')}</option>
              <option value="unlocked">{t('warehouse.periodLock.unlocked')}</option>
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.periodLock.year')}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Period Lock Table */}
      <PeriodLockTable
        warehouseId={selectedWarehouse}
        status={selectedStatus}
        year={selectedYear}
        onUnlockRequest={handleUnlockRequest}
        refreshTrigger={refreshTrigger}
      />

      {/* Create Lock Modal */}
      {isCreateModalOpen && (
        <CreateLockModal
          warehouses={warehouses}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Unlock Confirmation Modal */}
      {unlockModalLock && (
        <UnlockConfirmModal
          lock={unlockModalLock}
          onClose={() => setUnlockModalLock(null)}
          onSuccess={handleUnlockSuccess}
        />
      )}
    </div>
  );
};

export default PeriodLockPage;
